import * as React from 'react'

import { SitePermitStatus } from '~/client/graph'
import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import * as Icons from '~/client/src/shared/components/Icons'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import FileType from '~/client/src/shared/enums/FileType'
import { TagIconType } from '~/client/src/shared/enums/TagIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import { imageExtensions } from '~/client/src/shared/stores/domain/FileUploading.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

interface IGroupVisualTag {
  kind: 'tag'
  color: string
  labelColor?: string
  iconName: TagIconType
}

interface IGroupVisualIcon {
  kind: 'icon'
  icon: React.ElementType
  iconProps?: Record<string, unknown>
  infoTooltip?: string
}

interface IGroupVisualCustom {
  kind: 'custom'
  labelNode: React.ReactNode
}

interface IGroupVisualLocation {
  kind: 'location'
  dataObject: LocationBase
}

export type GroupVisualData =
  | IGroupVisualTag
  | IGroupVisualIcon
  | IGroupVisualCustom
  | IGroupVisualLocation

interface IProps {
  groupingKey: string
  categoryId: string
  permitTypesStore?: WorkflowTypesStore
  formCategoriesStore?: WorkflowCategoriesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
}

const normalizeGroupingKey = (groupingKey: string): string => {
  switch ((groupingKey || '').toLowerCase()) {
    case 'responsible':
      return 'contact'
    case 'filetype':
      return 'filetype'
    default:
      return (groupingKey || '').toLowerCase()
  }
}

const getFileTypeIconComponent = (categoryId: string): React.ElementType => {
  if (imageExtensions.includes(categoryId.toLowerCase())) {
    return Icons.FileImage
  }
  switch (categoryId) {
    case FileType.Pdf:
      return Icons.FilePdf
    case FileType.Video:
      return Icons.FileVideo
    case FileType.Word:
      return Icons.FileWord
    case FileType.Excel:
      return Icons.FileExcel
    case FileType.PowerPoint:
      return Icons.FilePowerpoint
    default:
      return Icons.FileImage
  }
}

export const getWorkflowGroupingVisualData = ({
  groupingKey,
  categoryId,
  permitTypesStore,
  formCategoriesStore,
  companiesStore,
  projectMembersStore,
  locationAttributesStore,
}: IProps): GroupVisualData | null => {
  if (!categoryId || categoryId === UNASSIGNED) {
    return null
  }

  switch (normalizeGroupingKey(groupingKey)) {
    case 'type': {
      const permitType = permitTypesStore?.getLastUpdatedTypeByType(categoryId)
      return {
        kind: 'icon',
        icon: WorkflowTypeIcon,
        iconProps: {
          iconName: permitType?.iconName,
          className: 'icon-20',
        },
      }
    }
    case 'status': {
      const status = categoryId as SitePermitStatus
      return {
        kind: 'custom',
        labelNode: React.createElement(StatusBadge, { status }),
      }
    }
    case 'category': {
      const isDefault =
        formCategoriesStore?.getById(categoryId)?.isDefaultCategory
      if (!isDefault) return null
      return {
        kind: 'icon',
        icon: Icons.LogoDarkSmall,
        iconProps: {},
        infoTooltip: Localization.translator.categoryCreatedByStruxHub,
      }
    }
    case 'company': {
      const company = companiesStore?.getCompanyById(categoryId)
      return {
        kind: 'icon',
        icon: Avatar,
        iconProps: {
          src: company?.avatarUrl,
          size: AvatarSize.XMinuscule,
        },
      }
    }
    case 'contact': {
      const user = projectMembersStore?.getById(categoryId)
      return {
        kind: 'icon',
        icon: Avatar,
        iconProps: {
          src: user?.avatarUrl,
          size: AvatarSize.XMinuscule,
        },
      }
    }
    case 'location':
    case 'equipment': {
      const location = locationAttributesStore?.getById(categoryId)
      if (location) {
        return { kind: 'location', dataObject: location }
      }
      return null
    }
    case 'filetype': {
      return {
        kind: 'icon',
        icon: getFileTypeIconComponent(categoryId),
        iconProps: {},
      }
    }
    default:
      return null
  }
}
